import React from 'react'
import { FormTypes, SubmissionTypes } from '@oneblink/types'
import { SetFormSubmission, submissionService } from '@oneblink/apps-react'
import { ExecutedLookups } from '@oneblink/apps-react'
import { toast } from 'react-toastify'
import _cloneDeep from 'lodash.clonedeep'
import jsum from 'jsum'

import useSubmitForm from '../../../hooks/useSubmitForm'
import useCancelForm from '../../../hooks/useCancelForm'
import useSaveDraft from '../../../hooks/useSaveDraft'
import useCheckMaintenanceOnSubmit from '../../../hooks/useCheckMaintenanceOnSubmit'
import MaintenanceModal from '../../../components/MaintenanceModel'
import useAutoSaveApprovalsControlledForm from '../../../hooks/useAutoSaveApprovalsControlledForm'
import AutoSaveApprovalsControlledForm from '../../../components/AutoSaveApprovalsControlledForm'

import { generateLicenceChargesTable } from '../../../lib/controlled-forms/food-licensing/licence-charges'
import {
  updateSpinnerState,
  setSpinnerInitial,
  setSpinnerError,
} from '../../../lib/controlled-forms/food-licensing/loading-spinner'
import { findElement } from '../../../lib/definition-manipulation'
import isDeepEqual from '../../../lib/is-deep-equal'
import {
  setHasPricingLookupRunInitial,
  updateHasPricingLookupRun,
  verifyPricingLookupHasRun,
} from '../../../lib/controlled-forms/common/has-pricing-lookup-run'
import getPricingAndCrn from '../../../lib/controlled-forms/common/get-pricing-and-crn'
import {
  generateFacilityLineItems,
  generateApplicationFeeLineItem,
  generateProductLineItems,
} from '../../../lib/controlled-forms/common/generate-invoice-line-items'
import {
  addClass,
  removeClass,
} from '../../../lib/controlled-forms/common/classes'

import getPricing from '../../../lib/controlled-forms/common/get-pricing'
import {
  populateContactElements,
  emergencyContact,
  updateEmergencyContactIsMobile,
  personCompletingFormDetails,
} from '../../../lib/controlled-forms/food-notification/contact'
import { populateBusinessContactsEmail } from '../../../lib/controlled-forms/common/contacts'
import cleanSubmissionModel from '../../../lib/clean-submission-model'
import { useAppConfig } from '../../../providers/AppConfigProvider'
import {
  getPointAddressPostalDeliveryType,
  SubmissionPointAddress,
} from '../../../lib/point-address'

interface FormProps {
  form: FormTypes.Form
  preFillData: Record<string, unknown>
}

function generatePartialModel(
  submission: Record<string, unknown>,
): Record<string, unknown> {
  return {
    facilityDetails: submission.facility_Details,
    businessType: submission.business_type,
    isNext: submission.IsNextToBeShown__exclude__hidden,
    confirmCompanyDetails: submission.confirmCompanyDetails,
  }
}

function generateHash({
  submission,
  definition,
}: {
  submission: SubmissionTypes.S3SubmissionData['submission']
  definition: FormTypes.Form
}): string {
  return jsum.digest(
    //need to clone deep becoz jsum corrupts the model somehow
    generatePartialModel(
      _cloneDeep(cleanSubmissionModel(submission, definition.elements)),
    ),
    'sha256',
    'hex',
  )
}

const pricingPaths = {
  permissionsPath: '/pricing/food-notification/permissions',
  productsPath: '/pricing/food-license/products',
  facilityPath: '/pricing/food-license/facility',
}

export default function FoodNotificationFormContainer({
  form,
  preFillData,
}: FormProps) {
  const customForm = React.useMemo(() => {
    setHasPricingLookupRunInitial(form, {})
    setSpinnerInitial(form)

    return form
  }, [form])

  const [partialModelHash, setPartialModelHash] = React.useState<string>()

  const [lookupError, setLookupError] = React.useState<string>()
  const [isLookupRunning, setIsLookupRunning] = React.useState(false)

  /*
    `customSetFormSubmission` is handed to every element in the form as its
    `onChange`. Reading these two values from state would put them in its
    dependency list, so the callback would get a new identity whenever either
    changed. Derived-value elements (calculation, summary) re-run their write
    effect on a new `onChange`, and a repeatable set rebuilds the whole entry
    for any write, so a changing identity here is enough to loop the form
    forever. Mirror them into refs and keep the callback stable.
  */
  const partialModelHashRef = React.useRef(partialModelHash)
  const isLookupRunningRef = React.useRef(isLookupRunning)

  const updatePartialModelHash = React.useCallback((hash: string) => {
    partialModelHashRef.current = hash
    setPartialModelHash(hash)
  }, [])

  const updateIsLookupRunning = React.useCallback((running: boolean) => {
    isLookupRunningRef.current = running
    setIsLookupRunning(running)
  }, [])

  const { buttons } = useAppConfig()

  const [onSaveDraft, isSavingDraft] = useSaveDraft({
    formDefinition: form,
    preFillData,
  })
  const [onCancel, cancelError] = useCancelForm(customForm)

  const [isGettingCharges, setIsGettingCharges] = React.useState<boolean>(false)
  const [
    { onSubmit, clearServerValidationMessage },
    { isSubmitting, serverValidationMessage },
  ] = useSubmitForm({
    formDefinition: customForm,
    preFillData,
    skipServerValidation: false,
  })

  const {
    definition,
    liveSubmission,
    setFormSubmission,
    handleSubmit,
    handleCancel,
    handleSaveDraft,
    isAutoSaveSubmissionAvailable,
    isLoadingAutoSaveSubmission,
    startNewSubmission,
    continueAutoSaveSubmission,
    executedLookups,
  } = useAutoSaveApprovalsControlledForm({
    form,
    initialSubmission: preFillData,
    onSubmit,
    onCancel,
    onSaveDraft,
  })

  const poBoxRegex = React.useMemo(() => /^po box/i, [])

  /* update definition here */
  const customDefinition = React.useMemo(() => {
    let def = definition
    def = generateLicenceChargesTable(liveSubmission, def)
    def = populateContactElements(liveSubmission, def)
    def = updateSpinnerState(liveSubmission, def)
    def = populateBusinessContactsEmail(liveSubmission, def)

    const facilityAddressSameAsMainElement = findElement(
      def,
      'facilty_Address_Same',
    )?.value as FormTypes.SelectElement

    if (facilityAddressSameAsMainElement) {
      facilityAddressSameAsMainElement.customCssClasses =
        liveSubmission.hide_facility_Address_Same
          ? addClass(
              'hidden',
              facilityAddressSameAsMainElement.customCssClasses,
            )
          : removeClass(
              'hidden',
              facilityAddressSameAsMainElement.customCssClasses,
            )
    }

    if (lookupError) {
      def = setSpinnerError(def, lookupError)
    }

    // pages are memo'd so we need new objects for the updated definition to render
    return { ...def, elements: def.elements.map((el) => ({ ...el })) }
  }, [definition, liveSubmission, lookupError])

  /* update submission data here */
  const customSetFormSubmission: SetFormSubmission = React.useCallback(
    (formSubmission) => {
      setFormSubmission((currentState) => {
        const newFormSubmission: {
          definition: FormTypes.Form
          submission: SubmissionTypes.S3SubmissionData['submission']
          lastElementUpdated: FormTypes.FormElement | undefined
          executedLookups: ExecutedLookups
        } =
          typeof formSubmission === 'function'
            ? formSubmission(currentState)
            : formSubmission

        if (
          newFormSubmission.submission.fauxPricingLookup__exclude ===
          'calculateCharges'
        ) {
          if (!isLookupRunningRef.current) {
            updateIsLookupRunning(true)
            setLookupError(undefined)
            getPricing({ ...newFormSubmission, ...pricingPaths })
              .then((result) => {
                if (result) {
                  setFormSubmission((prev) => {
                    return {
                      ...prev,
                      submission: {
                        ...prev.submission,
                        ...result,
                        fauxPricingLookup__exclude: undefined,
                      },
                    }
                  })
                }
              })
              .catch((e) => {
                if (e instanceof Error) {
                  setLookupError(e.message)
                }
                setFormSubmission((prev) => {
                  return {
                    ...prev,
                    submission: {
                      ...prev.submission,
                      fauxPricingLookup__exclude: undefined,
                      products: undefined,
                      paymentTotal__hidden: undefined,
                      facilities__hidden: undefined,
                      applicationFee__hidden: undefined,
                      applicationFeeTaxRate__hidden: undefined,
                      applicationFeePriorPaymentRequired__hidden: undefined,
                      hasPricingLookupRun__hidden: undefined,
                    },
                  }
                })
                console.error(e)
              })
              .finally(() => {
                updateIsLookupRunning(false)
              })
          }
        }

        updateEmergencyContactIsMobile(
          newFormSubmission.submission,
          newFormSubmission.definition,
        )

        emergencyContact(
          newFormSubmission.submission,
          newFormSubmission.definition,
        )

        personCompletingFormDetails(
          newFormSubmission.submission,
          newFormSubmission.definition,
        )

        // start PO BOX stuff
        let hideFaciltyAddressSame = false

        if (newFormSubmission.submission.Manual_Address) {
          const address = (
            (newFormSubmission.submission.Address_1 as string) ?? ''
          ).trim()
          hideFaciltyAddressSame = poBoxRegex.test(address)
        } else {
          const address = newFormSubmission.submission
            .Address as SubmissionPointAddress
          hideFaciltyAddressSame =
            (getPointAddressPostalDeliveryType(address) || '').toLowerCase() ===
            'po box'
        }

        newFormSubmission.submission.hide_facility_Address_Same =
          hideFaciltyAddressSame

        if (Array.isArray(newFormSubmission.submission.facility_Details)) {
          newFormSubmission.submission.facility_Details.forEach((facility) => {
            if (hideFaciltyAddressSame) {
              facility.facilty_Address_Same = 'No'
            }
          })
        }
        // end PO BOX stuff

        //always do this last
        updateHasPricingLookupRun({
          submission: newFormSubmission.submission,
          currentHash: partialModelHashRef.current,
          newHash: generateHash(newFormSubmission),
          updateHash: updatePartialModelHash,
        })

        /*
          Calculation elements re-run and write their result back whenever the
          `onChange` handed to them changes identity, even when the value is
          identical. Returning a new state object for one of those writes gives
          the renderer a new definition and a new `onChange` for every element,
          which runs the calculations again, and the form never settles. Keeping
          the current state when nothing actually changed breaks that cycle.

          `lastElementUpdated` is deliberately not compared: the competing
          calculations report different elements on every pass, so including it
          would mean never finding the states equal. A write that leaves the
          submission untouched has nothing for a lookup to react to anyway.
        */
        if (
          newFormSubmission.definition === currentState.definition &&
          isDeepEqual(newFormSubmission.submission, currentState.submission) &&
          isDeepEqual(
            newFormSubmission.executedLookups,
            currentState.executedLookups,
          )
        ) {
          return currentState
        }

        return { ...currentState, ...newFormSubmission }
      })
    },
    [
      setFormSubmission,
      updateIsLookupRunning,
      updatePartialModelHash,
      poBoxRegex,
    ],
  )

  const customHandleSubmission = React.useCallback(
    async (newFormSubmission: submissionService.NewFormSubmission) => {
      //taking all the precautions here and overengineering it
      const clonedNewFormSubmission = _cloneDeep(newFormSubmission)
      let pricingCompleted = false
      if (
        !verifyPricingLookupHasRun({
          newFormSubmission: clonedNewFormSubmission,
        })
      ) {
        return
      }

      setIsGettingCharges(true)
      try {
        const pricingFunc = () =>
          getPricing({ ...clonedNewFormSubmission, ...pricingPaths })

        const { pricingResponse, customerReferenceNumber } =
          await getPricingAndCrn(pricingFunc, 'NSW Food Authority')

        const facilityLineItems = generateFacilityLineItems(pricingResponse)
        const applicationFeeLineItem =
          generateApplicationFeeLineItem(pricingResponse)

        const productLineItems = generateProductLineItems(pricingResponse)

        clonedNewFormSubmission.submission = {
          ...clonedNewFormSubmission.submission,
          paymentTotal__hidden: pricingResponse.paymentTotal__hidden,
          customerReferenceNumber__hidden: customerReferenceNumber,
          lineItems: [
            ...facilityLineItems,
            applicationFeeLineItem,
            ...productLineItems,
          ],
        }
        pricingCompleted = true
      } catch (e) {
        console.error(e)
        toast.error('There was an error verifying payment amount', {
          position: 'bottom-right',
          hideProgressBar: true,
        })
      }

      setIsGettingCharges(false)

      //a Hail Mary to prevent premature submissions
      if (!pricingCompleted) {
        return
      }

      handleSubmit(clonedNewFormSubmission)
    },
    [handleSubmit],
  )

  const [
    { isFetchingMaintenanceWindow, fetchMaintenanceWindowError },
    { wrappedSubmitFn, clearMaintenanceMessage },
  ] = useCheckMaintenanceOnSubmit(customHandleSubmission)

  return (
    <>
      <AutoSaveApprovalsControlledForm
        definition={customDefinition}
        submission={liveSubmission}
        onCancel={handleCancel}
        onSubmit={wrappedSubmitFn}
        onSaveDraft={handleSaveDraft}
        setFormSubmission={customSetFormSubmission}
        buttons={buttons}
        isSubmitting={
          isSubmitting || isGettingCharges || isFetchingMaintenanceWindow
        }
        cancelError={cancelError}
        isAutoSaveSubmissionAvailable={isAutoSaveSubmissionAvailable}
        isLoadingAutoSaveSubmission={isLoadingAutoSaveSubmission}
        isSavingDraft={isSavingDraft}
        startNewSubmission={startNewSubmission}
        continueAutoSaveSubmission={continueAutoSaveSubmission}
        executedLookups={executedLookups}
        serverValidationMessage={serverValidationMessage}
        clearServerValidationMessage={clearServerValidationMessage}
      />
      <MaintenanceModal
        fetchMaintenanceWindowError={fetchMaintenanceWindowError}
        clearMaintenanceMessage={clearMaintenanceMessage}
      />
    </>
  )
}
